/**
 * Regulatory Landscape — Insights Page
 *
 * Answers: "What regulations and requirements do we need to keep in mind?"
 * Critical framing: assessment coverage ≠ compliance scores.
 */

import { useInsightsData } from '../hooks/useInsightsData'
import InsightCard from '../components/InsightCard'
import FrameworkCoverageTable from '../components/FrameworkCoverageTable'
import regulatoryUpdates from '../../../shared/data/regulatoryUpdates.json'

function RegulatoryLandscape({ assessmentId, client }) {
  const { data, raw, loading, error } = useInsightsData(assessmentId || (client?.assessments?.filter(a => a.status === 'completed')?.[0]?.id))

  // Filter regulatory updates by client industry
  const clientIndustry = (client?.industry || '').toLowerCase()
  const recentUpdates = (regulatoryUpdates?.findings || [])
    .filter(f => {
      if (!f.affectedIndustries || f.affectedIndustries.length === 0) return true
      return f.affectedIndustries.some(ind => clientIndustry.includes(ind.toLowerCase()) || ind.toLowerCase().includes(clientIndustry))
    })
    .sort((a, b) => new Date(b.publishedAt || b.detectedAt) - new Date(a.publishedAt || a.detectedAt))
    .slice(0, 5)

  const SEVERITY_COLORS = {
    high: 'border-l-red-500 bg-red-50/50 dark:bg-red-900/10',
    medium: 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10',
    low: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
  }

  // Framework coverage data
  const fwCoverage = raw?.frameworkCoverage
  const frameworks = fwCoverage?.frameworks || {}
  const frameworkList = Object.values(frameworks)

  // Build applicable framework table data
  const applicableFrameworks = client?.regulatory_frameworks || client?.regulatoryFrameworks || []

  // Key gaps by framework
  const gapsByFramework = frameworkList.filter(fw => fw.weakDomains?.length > 0).slice(0, 4)

  // Recommended actions
  const recommendations = []
  if (frameworkList.some(fw => fw.frameworkName === 'HIPAA' && fw.averageScore < 60)) {
    recommendations.push('Conduct a formal HIPAA Security Rule risk assessment to validate full compliance posture')
  }
  if (frameworkList.some(fw => fw.frameworkName === 'PCI DSS' && fw.averageScore < 60)) {
    recommendations.push('Complete PCI DSS SAQ self-assessment for payment card infrastructure')
  }
  if (frameworkList.some(fw => fw.frameworkName === 'CIS Controls' && fw.averageScore < 50)) {
    recommendations.push('Map CIS Controls Implementation Group 1 gaps to the enablement plan')
  }
  if (frameworkList.some(fw => fw.averageScore < 40)) {
    recommendations.push('Prioritize domain remediation for frameworks with assessment intersection below 40%')
  }
  if (recommendations.length === 0) {
    recommendations.push('Continue monitoring regulatory changes and maintain current maturity levels')
  }

  return (
    <div className="px-1 py-2 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Regulatory Landscape</h1>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
          Applicable frameworks, assessment coverage, and recent regulatory activity
        </p>
      </div>

      {/* Row 1: Framework coverage table */}
      {frameworkList.length > 0 && (
        <InsightCard title="Applicable Frameworks" subtitle="Assessment intersection with regulatory requirements" accent="#378ADD">
          <FrameworkCoverageTable
            frameworks={frameworks}
            disclaimer={fwCoverage?.summary?.assessmentCoverageDisclaimer}
          />
        </InsightCard>
      )}

      {frameworkList.length === 0 && applicableFrameworks.length === 0 && (
        <InsightCard title="Applicable Frameworks" accent="#378ADD">
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
            No regulatory frameworks have been identified for this client. Update the client's compliance selections in the assessment.
          </p>
        </InsightCard>
      )}

      {/* Row 2: Gaps + Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Key gaps by framework */}
        <InsightCard title="Key Gaps by Framework" accent="#EF4444">
          {gapsByFramework.length > 0 ? (
            <div className="space-y-3">
              {gapsByFramework.map(fw => (
                <div key={fw.frameworkName} className="flex items-start gap-3">
                  <span className="text-xs font-semibold text-gray-900 dark:text-white flex-shrink-0 w-20">{fw.frameworkName}</span>
                  <div className="flex flex-wrap gap-1">
                    {fw.weakDomains.slice(0, 3).map(d => (
                      <span key={d} className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No significant gaps identified</p>
          )}
        </InsightCard>

        {/* Recommended actions */}
        <InsightCard title="Recommended Actions" accent="#1D9E75">
          <div className="space-y-3">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-[10px] font-bold text-teal-700 dark:text-teal-400 mt-0.5">
                  {idx + 1}
                </span>
                <p className="text-sm text-gray-700 dark:text-gray-300">{rec}</p>
              </div>
            ))}
          </div>
        </InsightCard>
      </div>

      {/* Row 3: Regulatory updates */}
      <InsightCard title="Recent Regulatory Activity" subtitle={`Filtered for ${client?.industry || 'your industry'}`} accent="#C9A94E">
        {recentUpdates.length > 0 ? (
          <div className="space-y-3">
            {recentUpdates.map(update => (
              <div key={update.id} className={`p-3 rounded-lg border-l-[3px] ${SEVERITY_COLORS[update.severity] || SEVERITY_COLORS.low}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{update.title}</p>
                    {update.summary && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{update.summary}</p>}
                  </div>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                    {update.publishedAt ? new Date(update.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No recent regulatory changes affecting your industry</p>
        )}
      </InsightCard>
    </div>
  )
}

export default RegulatoryLandscape
